import { FC, useState, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import DatePicker, { registerLocale } from 'react-datepicker';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { enUS } from 'date-fns/locale';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './TripCalendar.module.scss';
import { TripDetail } from '../../../../../../../types/trip.types';
import { formatDateToLocal } from '../../../../../../../utils/dateUtils';
import { FilterIcon, ExpandIcon } from '../../../../../../../components/Icons';

const customLocale = {
  ...enUS,
  localize: {
    ...enUS.localize,
    day: (n: number) => ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][n],
  },
};

registerLocale('custom', customLocale);

interface TripCalendarProps {
  trip: TripDetail;
  selectedDate?: Date;
  onDateSelect?: (date: Date | null) => void;
  onShowFilters: () => void;
  onTogglePanel: () => void;
  showCloseButton?: boolean;
  isMobile?: boolean;
}

const TripCalendar: FC<TripCalendarProps> = ({
  trip,
  selectedDate = new Date(),
  onDateSelect,
  onShowFilters,
  onTogglePanel,
  showCloseButton = false,
  isMobile = false,
}) => {
  const [currentDate, setCurrentDate] = useState(selectedDate);
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);

  const parseDayKeyToDate = (dayKey: string): Date => {
    try {
      const parts = dayKey.split('-');
      if (parts.length >= 3) {
        const month = parts[1];
        const day = parts[2];

        const tripYear = new Date(trip.start_date).getFullYear();

        const dateString = `${month} ${day}, ${tripYear}`;
        const parsedDate = new Date(dateString);

        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      }
    } catch (error) {
      console.warn('Error parsing day key:', dayKey, error);
    }

    return new Date(trip.start_date);
  };

  const activityDates = useMemo(() => {
    const datesWithActivities = new Map<string, any[]>();

    if (!trip?.trip_stops_by_day) return datesWithActivities;

    try {
      Object.entries(trip.trip_stops_by_day).forEach(([dayKey, activities]) => {
        if (activities && Array.isArray(activities) && activities.length > 0) {
          const dayDate = parseDayKeyToDate(dayKey);
          const dateKey = formatDateToLocal(dayDate);
          datesWithActivities.set(dateKey, activities);
        }
      });
    } catch (error) {
      console.warn('Error processing activity dates:', error);
    }

    return datesWithActivities;
  }, [trip?.trip_stops_by_day, trip?.start_date]);

  const hasActivities = (date: Date): boolean => {
    try {
      const dateKey = formatDateToLocal(date);
      return activityDates.has(dateKey) && (activityDates.get(dateKey)?.length || 0) > 0;
    } catch (error) {
      console.warn('Error checking activities for date:', error);
      return false;
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setCurrentDate(date);
      onDateSelect?.(date);
      if (showMonthYearPicker) {
        setShowMonthYearPicker(false);
      }
    }
  };

  const handleMonthChange = (date: Date) => {
    if (date) {
      setCurrentDate(date);
    }
  };

  const toggleMonthYearPicker = () => {
    setShowMonthYearPicker(!showMonthYearPicker);
  };

  const CustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: any) => {
    return (
      <Box className={styles.calendarHeader}>
        <Box
          className={styles.monthYearSection}
          onClick={toggleMonthYearPicker}
        >
          <Typography
            variant="h9Semibold"
            className={styles.monthYear}
          >
            {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Typography>
          <ChevronRightIcon className={`${styles.dropdownIcon} ${showMonthYearPicker ? styles.rotated : ''}`} />
        </Box>

        <Box className={styles.headerRight}>
          <IconButton
            onClick={() => {
              if (decreaseMonth) decreaseMonth();
            }}
            className={styles.navButton}
            size="small"
            disabled={prevMonthButtonDisabled}
          >
            <ChevronLeftIcon />
          </IconButton>

          <IconButton
            onClick={() => {
              if (increaseMonth) increaseMonth();
            }}
            className={styles.navButton}
            size="small"
            disabled={nextMonthButtonDisabled}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box className={styles.tripCalendarWrapper}>
      {!isMobile && (
        <Box className={styles.externalHeader}>
          {showCloseButton ? (
            <IconButton
              onClick={onTogglePanel}
              className={styles.closeButton}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          ) : (
            <IconButton
              onClick={onTogglePanel}
              className={styles.expandButton}
              size="small"
            >
              <ExpandIcon
                className={styles.expandIcon}
                size={32}
              />
            </IconButton>
          )}

          <Box
            onClick={onShowFilters}
            className={styles.filterButton}
          >
            <FilterIcon size={16} />
            <Typography variant="h11">Filters</Typography>
          </Box>
        </Box>
      )}

      <DatePicker
        selected={currentDate}
        onChange={handleDateChange}
        onMonthChange={handleMonthChange}
        inline
        showPopperArrow={false}
        calendarClassName={styles.customTripCalendar}
        renderCustomHeader={CustomHeader}
        locale="custom"
        showMonthYearPicker={showMonthYearPicker}
        dayClassName={date => {
          const hasActivity = hasActivities(date);
          const isToday = date.toDateString() === new Date().toDateString();

          let classes = 'customDay';
          if (hasActivity) classes += ' dayWithTrip';
          if (isToday) classes += ' dayToday';

          return classes;
        }}
      />
    </Box>
  );
};

export default TripCalendar;
